import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import type { RootState } from 'App/Store';
import api from 'service';

import { getFormattedFormData } from 'modules/form/selectors';
import selfServicesApi from 'modules/selfServices/service';

import { UPDATE_FIXED_CACHE_KEY } from './constants';
import type { SelfService } from './types/SelfService';
import type { FinalizeSelfServiceRequest } from './types/FinalizeSelfServiceRequest';

// A mutation with a `queryFn` (instead of a thunk) only orchestrates existing endpoints: it keeps the finalization
// status in the RTK Query cache (shared through a fixed cache key), without any custom slice state.
// This status is pending as soon as the mutation is initiated, so the final instructions can wait for it.
const finalizeApi = api.injectEndpoints({
  endpoints: (build) => ({
    submitFinalization: build.mutation<SelfService, FinalizeSelfServiceRequest>({
      async queryFn({ id, elapsedTime }, { dispatch, getState }) {
        try {
          const selfService = await dispatch(selfServicesApi.endpoints.getSelfServiceById.initiate({ id })).unwrap();
          const formattedData = getFormattedFormData(getState() as RootState, selfService);
          const updatedSelfService = await dispatch(selfServicesApi.endpoints.updateSelfService.initiate({
            id,
            body: {
              ...selfService,
              ...formattedData,
              elapsedTime,
            },
          }, { fixedCacheKey: UPDATE_FIXED_CACHE_KEY })).unwrap();

          return { data: updatedSelfService };
        } catch (error) {
          const isRequestError = typeof error === 'object' && error !== null && 'status' in error;
          return {
            error: isRequestError
              ? error as FetchBaseQueryError
              : { status: 'CUSTOM_ERROR', error: (error as SerializedError).message ?? 'Finalization failed' },
          };
        }
      },
    }),
  }),
});

export const { useSubmitFinalizationMutation } = finalizeApi;

export default finalizeApi;
